import React, {
  useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Box, Dialog, DialogActions, DialogContent, DialogContentText, FormControlLabel, IconButton,
  Snackbar, Alert, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  CardWrapper, SectionTitle, ButtonOutlined, ButtonUI, FieldLabel, formatBRL,
} from '../../UI';
import * as C from '../../../theme/colors.constants';
import { toBrazilianDate } from '../../../../helpers/utils/utils';
import { calcularTotalCustas, removerTagsHtml } from '../Juridico/juridicoController';
import {
  salvarInformacoesDadosJuridicos, lancarCausaDadosJuridicos,
  excluirCustasDadosJuridicos, lancarCustaDadosJuridicos,
} from '../../../../services/core/processos/processoDadosJuridicosService';
import useSessaoDialogMutacao from '../../../hooks/useSessaoDialogMutacao';
import DialogNovoCustas from '../Juridico/DialogNovoCustas';
import DialogEditarCustas from '../Juridico/DialogEditarCustas';

/**
 * Aba Jurídico — print `grid_check_juridico.png`.
 *
 * Dois cards: "Informações" (com o switch de advogado externo, o textarea do
 * detalhe junto ao tribunal e os dois botões de honorários) e "Custas" (tabela
 * com o total no rodapé e o botão "+ NOVO").
 *
 * AW2-1108 Etapa 0: `dadosJuridicos` (DTO cru de `findDadosJuridicos`, buscado
 * pelo container) substitui o mock estático local — leitura real dos 2 blocos.
 *
 * `detalhesValorCausa` vem de um editor rich-text real e pode conter HTML —
 * sempre stripado via `removerTagsHtml` antes de popular o campo, nunca
 * `dangerouslySetInnerHTML` (mesmo padrão de `clienteController.removerTagsHtml`).
 *
 * AW2-1108 Etapa 1: modo de edição real do bloco "Informações" — port de
 * `onClickConfirmarInformacoes`/`onClickCancelarInformacoes`/
 * `salvarInformacoesDadosJuridicos` reais (`Informacoes.jsx`). Card sempre
 * montado (nunca desmonta ao trocar de aba/processo), então o guard de
 * sessão segue o padrão de `BlocoAnotacao.jsx`/`AbaDevedor.jsx`
 * (`sessaoRef`/`enviandoRef`, incrementado no efeito de troca de
 * `idProcesso` — não há "abrir"/"fechar" aqui, diferente de
 * `useSessaoDialogMutacao`). Gate do lápis idêntico ao real:
 * `estarLancadoValorCausaInicial || estarLancadoValorCausaFinal ||
 * !cobrancaJudicial || !hasPermissoes`; ícones de edição (lápis/cancelar/
 * salvar) e os botões de honorários ficam ocultos quando `ehFaseAcordoSeguro`
 * (mesmo `{!ehFaseAcordoSeguro && (...)}` real) — os campos em si não são
 * escondidos (mesmo comportamento real: ficam sempre visíveis, só nunca
 * habilitáveis nesse caso, já que o lápis some).
 *
 * A tela real NÃO valida "Valor da Causa" no client antes do submit (o
 * asterisco é só decoração — `getRequiredLabel(...,true)`) — decisão
 * confirmada com o usuário: replicar fielmente essa ausência de guard, sem
 * bloqueio novo. Também confirmado por leitura direta de
 * `onClickConfirmarInformacoes` real: tanto o `try` quanto o `catch` saem do
 * modo de edição (`setIsEditing(!isEditing)` incondicional) — replicado
 * abaixo, mesmo no caminho de erro.
 *
 * AW2-1108 Etapa 2: "Lançar Honorários Iniciais"/"Lançar Honorários Finais" —
 * port de `onClickButtonLancarInicial`/`onClickButtonLancarFinal` reais
 * (`lancarCausaDadosJuridicos`). A tela real usa 2 `OptionDialogNew`
 * independentes (`optionalDialogLancarInicial`/`optionalDialogLancarFinal`);
 * consolidado aqui num único dialog (`dialogHonorarioTipo`, `null | 'inicial'
 * | 'final'`) — mesma ação parametrizada por `serFinal`, mesmo padrão "1
 * dialog, N modos" já convencionado no módulo (`DialogTextoPadrao.jsx`) em
 * vez de duplicar o par estado+dialog. Guard de sessão via
 * `useSessaoDialogMutacao` (ciclo `open`/`close` real, diferente do guard
 * manual da Etapa 1) — independente do guard de "Salvar" Informações (mesma
 * separação de `AbaDevedor.jsx`: 2 mutações que podem, em tese, estar em voo
 * ao mesmo tempo não compartilham o mesmo `enviando`/Snackbar). `finally`
 * fecha o dialog e chama `onJuridicoMutado()` incondicionalmente — mesmo
 * `finally` incondicional real (`updatePageInformacoes()` sempre), seguro
 * porque o refetch já tem seu próprio guard `idProcessoRef` no container.
 * `codResult === 0` → Snackbar de sucesso; senão → Snackbar de aviso com
 * `msgResult` (mensagem de negócio do backend); exceção de rede → Snackbar
 * de erro genérico (mesmos 3 textos/severidades da tela real).
 *
 * AW2-1108 Etapa 3: botão "+ Novo" do bloco "Custas" — port de
 * `DialogNovoCustas.jsx` real (`Juridico/DialogNovoCustas.jsx`, novo
 * arquivo). Diferente de Honorários (Etapa 2), o `onJuridicoMutado()` só é
 * chamado no SUCESSO — confirmado por leitura direta: o `catch` real
 * também fecha o dialog, mas NÃO chama `updatePage()`, então não replicar o
 * `finally` incondicional da Etapa 2 aqui.
 *
 * AW2-1108 Etapa 4: as 3 ações de linha da grid de Custas (Lançar valor/
 * Editar/Excluir) — port de `TableBodyCustas.jsx` real. "Lançar valor" e
 * "Excluir" consolidados num único dialog de confirmação
 * (`acaoCustaAtiva: { tipo: 'lancar'|'excluir', custa } | null`, mesmo
 * padrão "1 dialog, N modos" já usado para Honorários na Etapa 2) —
 * "Editar" usa `Juridico/DialogEditarCustas.jsx` (novo, mesmo padrão
 * `open`/`content` de `DialogNovoCustas.jsx`). As 3 ações compartilham o
 * mesmo gate: `content.estarLancado || !cobrancaJudicial || !hasPermissoes
 * || ehFaseAcordoSeguro` (idêntico ao real, duplicado no handler além do
 * `disabled`). "Lançar valor" tem `finally` incondicional real (fecha e
 * refaz o fetch mesmo em erro, igual Honorários); "Excluir" só refaz o
 * fetch no sucesso (igual "+ Novo"/"Editar") — as 2 assinaturas divergentes
 * de refetch real, replicadas fielmente em 2 handlers separados
 * (`onConfirmarLancarValorCusta`/`onConfirmarExcluirCusta`), não uma única
 * função genérica. "Excluir" reenvia `dataLancamento`/`descricao`/`valor`
 * da linha no payload (não só o id) — mesma redundância do service real,
 * replicada sem "corrigir".
 */
export default function AbaJuridico({
  dadosJuridicos, idProcesso, onJuridicoMutado, cobrancaJudicial, ehFaseAcordoSeguro, hasPermissoes,
}) {
  const [editando, setEditando] = useState(false);
  const [estarComAdvogadoExterno, setEstarComAdvogadoExterno] = useState(false);
  const [detalhe, setDetalhe] = useState('');
  const [valorCausa, setValorCausa] = useState(0);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  const [dialogHonorarioTipo, setDialogHonorarioTipo] = useState(null);
  const [enviandoHonorario, setEnviandoHonorario] = useState(false);
  const [feedbackHonorario, setFeedbackHonorario] = useState(null);
  const [novoCustaAberto, setNovoCustaAberto] = useState(false);
  const [feedbackCusta, setFeedbackCusta] = useState(null);
  const [acaoCustaAtiva, setAcaoCustaAtiva] = useState(null);
  const [enviandoAcaoCusta, setEnviandoAcaoCusta] = useState(false);
  const [custaEmEdicao, setCustaEmEdicao] = useState(null);

  const sessaoRef = useRef(0);
  const enviandoRef = useRef(false);
  const idProcessoAnteriorRef = useRef(idProcesso);

  const { sessaoRef: sessaoHonorarioRef, enviandoRef: enviandoHonorarioRef } = useSessaoDialogMutacao(
    dialogHonorarioTipo !== null, idProcesso,
  );
  const { sessaoRef: sessaoAcaoCustaRef, enviandoRef: enviandoAcaoCustaRef } = useSessaoDialogMutacao(
    acaoCustaAtiva !== null, idProcesso,
  );

  function restaurarCampos() {
    setEstarComAdvogadoExterno(dadosJuridicos?.estarComAdvogadoExterno ?? false);
    setDetalhe(removerTagsHtml(dadosJuridicos?.detalhesValorCausa));
    setValorCausa(dadosJuridicos?.valorCausa ?? 0);
  }

  useEffect(() => {
    setEditando(false);
    setEstarComAdvogadoExterno(dadosJuridicos?.estarComAdvogadoExterno ?? false);
    setDetalhe(removerTagsHtml(dadosJuridicos?.detalhesValorCausa));
    setValorCausa(dadosJuridicos?.valorCausa ?? 0);
  }, [dadosJuridicos]);

  // Guard de sessão — troca de processo invalida qualquer submit em voo,
  // mesmo no cenário A→B→A (comparação simples "idProcesso ainda é o mesmo"
  // não bastaria, ver `BlocoAnotacao.jsx`/`AbaDevedor.jsx` no
  // `src/prototipo/CLAUDE.md`).
  useEffect(() => {
    if (idProcessoAnteriorRef.current !== idProcesso) {
      sessaoRef.current += 1;
      enviandoRef.current = false;
      setEnviando(false);
      setErro('');
      setSucesso('');
      // `useSessaoDialogMutacao` já reseta seu próprio `sessaoRef`/`enviandoRef`
      // ao trocar de `idProcesso` — este componente ainda precisa resetar o
      // estado de UI que só ele possui (qual dialog está aberto, feedback).
      setDialogHonorarioTipo(null);
      setEnviandoHonorario(false);
      setFeedbackHonorario(null);
      setNovoCustaAberto(false);
      setFeedbackCusta(null);
      setAcaoCustaAtiva(null);
      setEnviandoAcaoCusta(false);
      setCustaEmEdicao(null);
      idProcessoAnteriorRef.current = idProcesso;
    }
  }, [idProcesso]);

  function onClickEditar() {
    if (
      !cobrancaJudicial
      || !hasPermissoes
      || dadosJuridicos?.estarLancadoValorCausaInicial
      || dadosJuridicos?.estarLancadoValorCausaFinal
    ) {
      return;
    }
    setEditando(true);
  }

  function onClickCancelar() {
    setEditando(false);
    restaurarCampos();
  }

  async function onClickSalvar() {
    if (!idProcesso || enviandoRef.current) {
      return;
    }

    const sessaoAlvo = sessaoRef.current;
    enviandoRef.current = true;
    setEnviando(true);
    setErro('');

    try {
      await salvarInformacoesDadosJuridicos(valorCausa, detalhe, estarComAdvogadoExterno, idProcesso);
      if (sessaoRef.current !== sessaoAlvo) {
        return;
      }
      setEditando(false);
      setSucesso('Informações atualizadas com sucesso.');
      if (onJuridicoMutado) {
        onJuridicoMutado();
      }
    } catch {
      if (sessaoRef.current !== sessaoAlvo) {
        return;
      }
      // Mesmo `onClickConfirmarInformacoes` real: sai do modo de edição
      // também no erro, sem reverter os campos para o dado original — o que
      // o usuário digitou continua visível, só desabilitado.
      setEditando(false);
      setErro('Não foi possível salvar as informações dos dados jurídicos. Tente novamente.');
    } finally {
      if (sessaoRef.current === sessaoAlvo) {
        enviandoRef.current = false;
        setEnviando(false);
      }
    }
  }

  const onFecharErro = () => setErro('');
  const onFecharSucesso = () => setSucesso('');

  // Gate idêntico ao real, parametrizado por `tipo` em vez de duplicado —
  // ver comentário do componente sobre a consolidação num único dialog.
  function honorarioBloqueado(tipo) {
    const estarLancado = tipo === 'final'
      ? dadosJuridicos?.estarLancadoValorCausaFinal
      : dadosJuridicos?.estarLancadoValorCausaInicial;
    const porcentagem = tipo === 'final'
      ? dadosJuridicos?.porcentagemHonorarioContratualFinal
      : dadosJuridicos?.porcentagemHonorarioContratualInicial;
    return Boolean(estarLancado) || !cobrancaJudicial || !hasPermissoes || porcentagem === 0;
  }

  function onClickLancarHonorario(tipo) {
    if (honorarioBloqueado(tipo)) {
      return;
    }
    setDialogHonorarioTipo(tipo);
  }

  function onCancelarDialogHonorario() {
    if (enviandoHonorarioRef.current) {
      return;
    }
    setDialogHonorarioTipo(null);
  }

  async function onConfirmarLancarHonorario() {
    if (!idProcesso || enviandoHonorarioRef.current || dialogHonorarioTipo === null) {
      return;
    }

    const serFinal = dialogHonorarioTipo === 'final';
    const sessaoAlvo = sessaoHonorarioRef.current;
    enviandoHonorarioRef.current = true;
    setEnviandoHonorario(true);

    try {
      const response = await lancarCausaDadosJuridicos(
        serFinal, valorCausa, detalhe, estarComAdvogadoExterno, idProcesso,
      );
      if (sessaoHonorarioRef.current === sessaoAlvo) {
        if (response?.data?.codResult === 0) {
          setFeedbackHonorario({ severidade: 'success', texto: 'Valor enviado com sucesso.' });
        } else {
          setFeedbackHonorario({ severidade: 'warning', texto: response?.data?.msgResult });
        }
      }
    } catch {
      if (sessaoHonorarioRef.current === sessaoAlvo) {
        setFeedbackHonorario({
          severidade: 'error',
          texto: 'Não foi possível enviar o valor dos dados jurídicos. Tente novamente.',
        });
      }
    } finally {
      // Mesmo `finally` incondicional real (`updatePageInformacoes()` sempre)
      // — seguro mesmo fora da sessão alvo porque o refetch já tem seu
      // próprio guard `idProcessoRef` no container.
      setDialogHonorarioTipo(null);
      if (onJuridicoMutado) {
        onJuridicoMutado();
      }
      if (sessaoHonorarioRef.current === sessaoAlvo) {
        enviandoHonorarioRef.current = false;
        setEnviandoHonorario(false);
      }
    }
  }

  const onFecharFeedbackHonorario = () => setFeedbackHonorario(null);

  function onClickNovaCusta() {
    if (!cobrancaJudicial || !hasPermissoes) {
      return;
    }
    setNovoCustaAberto(true);
  }

  function onFecharDialogNovaCusta() {
    setNovoCustaAberto(false);
  }

  // `onJuridicoMutado()` só no sucesso — diferente do `finally` incondicional
  // de Honorários (Etapa 2), o real de Custas não refaz o fetch em erro.
  function onSucessoNovaCusta() {
    setFeedbackCusta({ severidade: 'success', texto: 'Custa incluída com sucesso.' });
    if (onJuridicoMutado) {
      onJuridicoMutado();
    }
  }

  function onErroNovaCusta(mensagem) {
    setFeedbackCusta({ severidade: 'error', texto: mensagem });
  }

  const onFecharFeedbackCusta = () => setFeedbackCusta(null);

  // Mesmo gate real (`content.estarLancado || !cobrancaJudicial ||
  // !hasPermissoes || ehFaseAcordoSeguro`), idêntico nas 3 ações da linha.
  function acaoCustaBloqueada(custa) {
    return Boolean(custa?.estarLancado) || !cobrancaJudicial || !hasPermissoes || Boolean(ehFaseAcordoSeguro);
  }

  function onClickLancarValorCusta(custa) {
    if (acaoCustaBloqueada(custa)) {
      return;
    }
    setAcaoCustaAtiva({ tipo: 'lancar', custa });
  }

  function onClickExcluirCusta(custa) {
    if (acaoCustaBloqueada(custa)) {
      return;
    }
    setAcaoCustaAtiva({ tipo: 'excluir', custa });
  }

  function onClickEditarCusta(custa) {
    if (acaoCustaBloqueada(custa)) {
      return;
    }
    setCustaEmEdicao(custa);
  }

  function onCancelarAcaoCusta() {
    if (enviandoAcaoCustaRef.current) {
      return;
    }
    setAcaoCustaAtiva(null);
  }

  async function onConfirmarExcluirCusta() {
    if (!idProcesso || enviandoAcaoCustaRef.current || acaoCustaAtiva === null) {
      return;
    }

    const { custa } = acaoCustaAtiva;
    const sessaoAlvo = sessaoAcaoCustaRef.current;
    enviandoAcaoCustaRef.current = true;
    setEnviandoAcaoCusta(true);

    try {
      // Reenvia `dataLancamento`/`descricao`/`valor` da linha (não só o id)
      // — mesma redundância do service real, replicada fielmente.
      await excluirCustasDadosJuridicos(
        custa.idProcessoCustas, custa.dataLancamento, custa.descricao, custa.valor, idProcesso,
      );
      if (sessaoAcaoCustaRef.current === sessaoAlvo) {
        setAcaoCustaAtiva(null);
        setFeedbackCusta({ severidade: 'success', texto: 'Lançamento excluído com sucesso.' });
        if (onJuridicoMutado) {
          onJuridicoMutado();
        }
      }
    } catch {
      if (sessaoAcaoCustaRef.current === sessaoAlvo) {
        setAcaoCustaAtiva(null);
        setFeedbackCusta({
          severidade: 'error',
          texto: 'Não foi possível excluir o lançamento. Tente novamente.',
        });
      }
    } finally {
      if (sessaoAcaoCustaRef.current === sessaoAlvo) {
        enviandoAcaoCustaRef.current = false;
        setEnviandoAcaoCusta(false);
      }
    }
  }

  async function onConfirmarLancarValorCusta() {
    if (!idProcesso || enviandoAcaoCustaRef.current || acaoCustaAtiva === null) {
      return;
    }

    const { custa } = acaoCustaAtiva;
    const sessaoAlvo = sessaoAcaoCustaRef.current;
    enviandoAcaoCustaRef.current = true;
    setEnviandoAcaoCusta(true);

    try {
      const response = await lancarCustaDadosJuridicos(custa.idProcessoCustas);
      if (sessaoAcaoCustaRef.current === sessaoAlvo) {
        if (response?.data?.codResult === 0) {
          setFeedbackCusta({ severidade: 'success', texto: 'Valor enviado com sucesso.' });
        } else {
          setFeedbackCusta({ severidade: 'warning', texto: response?.data?.msgResult });
        }
      }
    } catch {
      if (sessaoAcaoCustaRef.current === sessaoAlvo) {
        setFeedbackCusta({
          severidade: 'error',
          texto: 'Não foi possível enviar o valor dos dados jurídicos. Tente novamente.',
        });
      }
    } finally {
      // Mesmo `finally` incondicional real (fecha e refaz o fetch mesmo em
      // erro) — diferente de "Excluir" acima, que só refaz no sucesso.
      setAcaoCustaAtiva(null);
      if (onJuridicoMutado) {
        onJuridicoMutado();
      }
      if (sessaoAcaoCustaRef.current === sessaoAlvo) {
        enviandoAcaoCustaRef.current = false;
        setEnviandoAcaoCusta(false);
      }
    }
  }

  function onConfirmarAcaoCusta() {
    if (acaoCustaAtiva?.tipo === 'excluir') {
      onConfirmarExcluirCusta();
    } else if (acaoCustaAtiva?.tipo === 'lancar') {
      onConfirmarLancarValorCusta();
    }
  }

  function onFecharDialogEditarCusta() {
    setCustaEmEdicao(null);
  }

  function onSucessoEditarCusta() {
    setFeedbackCusta({ severidade: 'success', texto: 'Lançamento alterado com sucesso.' });
    if (onJuridicoMutado) {
      onJuridicoMutado();
    }
  }

  function onErroEditarCusta(mensagem) {
    setFeedbackCusta({ severidade: 'error', texto: mensagem });
  }

  const custas = dadosJuridicos?.dadosJuridicosCustasQueryList ?? [];
  const totalCustas = calcularTotalCustas(custas);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'clamp(10px, 0.8vw, 22px)' }}>
      <CardWrapper>
        <SectionTitle
          right={!ehFaseAcordoSeguro && (
            editando ? (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Cancelar" arrow>
                  <span>
                    <IconButton
                      size="small"
                      onClick={onClickCancelar}
                      disabled={enviando}
                      aria-label="Cancelar edição das informações"
                      sx={{
                        color: C.navy,
                        border: `1px solid ${C.borderGrey}`,
                        '&:hover': { backgroundColor: C.paleGrey },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Salvar" arrow>
                  <span>
                    <IconButton
                      size="small"
                      onClick={onClickSalvar}
                      disabled={enviando}
                      aria-label="Salvar informações"
                      sx={{
                        color: C.white,
                        backgroundColor: C.navy,
                        border: `1px solid ${C.borderGrey}`,
                        '&:hover': { backgroundColor: C.navyDark },
                      }}
                    >
                      <CheckIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            ) : (
              <Tooltip title="Editar informações" arrow>
                <span>
                  <IconButton
                    size="small"
                    onClick={onClickEditar}
                    disabled={
                      !cobrancaJudicial
                      || !hasPermissoes
                      || dadosJuridicos?.estarLancadoValorCausaInicial
                      || dadosJuridicos?.estarLancadoValorCausaFinal
                    }
                    aria-label="Editar informações"
                    sx={{
                      color: C.navy,
                      border: `1px solid ${C.borderGrey}`,
                      '&:hover': { backgroundColor: C.paleGrey },
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )
          )}
        >
          Informações
        </SectionTitle>

        <FormControlLabel
          control={(
            <Switch
              checked={estarComAdvogadoExterno}
              disabled={!editando || enviando}
              onChange={e => setEstarComAdvogadoExterno(e.target.checked)}
            />
          )}
          label={<Typography variant="body2">Advogado Externo Envolvido</Typography>}
          sx={{ mb: 1.5 }}
        />

        <Box sx={{ mb: 2 }}>
          <FieldLabel sx={{ fontWeight: 400, color: C.warmGrey }}>
            Detalhe do Processo Junto ao Tribunal
          </FieldLabel>
          <TextField
            fullWidth
            multiline
            minRows={6}
            disabled={!editando || enviando}
            value={detalhe}
            onChange={e => setDetalhe(e.target.value)}
            sx={{ '& .MuiInputBase-root.Mui-disabled': { backgroundColor: '#dcdcdc' } }}
          />
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
            gap: 'clamp(10px, 0.8vw, 22px)',
            alignItems: 'end',
          }}
        >
          <Box>
            <FieldLabel sx={{ fontWeight: 400, color: C.warmGrey }}>Valor da Causa*</FieldLabel>
            <TextField
              fullWidth
              size="small"
              disabled={!editando || enviando}
              value={formatBRL(valorCausa)}
              onChange={e => setValorCausa(e.target.value)}
              sx={{ '& .MuiInputBase-root.Mui-disabled': { backgroundColor: '#dcdcdc' } }}
            />
          </Box>
          {!editando && !ehFaseAcordoSeguro && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <ButtonOutlined
                startIcon={<AccountBalanceIcon />}
                disabled={honorarioBloqueado('inicial')}
                onClick={() => onClickLancarHonorario('inicial')}
              >
                LANÇAR HONORÁRIOS INICIAIS
              </ButtonOutlined>
              <ButtonOutlined
                startIcon={<AccountBalanceIcon />}
                disabled={honorarioBloqueado('final')}
                onClick={() => onClickLancarHonorario('final')}
              >
                LANÇAR HONORÁRIOS FINAIS
              </ButtonOutlined>
            </Box>
          )}
        </Box>
      </CardWrapper>

      <Dialog
        open={dialogHonorarioTipo !== null}
        onClose={enviandoHonorario ? undefined : onCancelarDialogHonorario}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ pt: 4 }}>
          <DialogContentText sx={{ textAlign: 'center', fontSize: 16 }}>
            {dialogHonorarioTipo === 'final'
              ? 'Confirma o lançamento do valor do honorário contratual final na Conta Corrente do Cliente?'
              : 'Confirma o lançamento do valor do honorário contratual inicial na Conta Corrente do Cliente?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <ButtonOutlined onClick={onCancelarDialogHonorario} disabled={enviandoHonorario}>Cancelar</ButtonOutlined>
          <ButtonUI onClick={onConfirmarLancarHonorario} disabled={enviandoHonorario}>Confirmar</ButtonUI>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(erro)}
        autoHideDuration={4000}
        onClose={onFecharErro}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={onFecharErro}>
          {erro}
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(sucesso)}
        autoHideDuration={4000}
        onClose={onFecharSucesso}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={onFecharSucesso}>
          {sucesso}
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(feedbackHonorario)}
        autoHideDuration={4000}
        onClose={onFecharFeedbackHonorario}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={feedbackHonorario?.severidade ?? 'info'}
          variant="filled"
          onClose={onFecharFeedbackHonorario}
        >
          {feedbackHonorario?.texto}
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(feedbackCusta)}
        autoHideDuration={4000}
        onClose={onFecharFeedbackCusta}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={feedbackCusta?.severidade ?? 'info'}
          variant="filled"
          onClose={onFecharFeedbackCusta}
        >
          {feedbackCusta?.texto}
        </Alert>
      </Snackbar>

      <DialogNovoCustas
        open={novoCustaAberto}
        idProcesso={idProcesso}
        onClose={onFecharDialogNovaCusta}
        onSucesso={onSucessoNovaCusta}
        onErro={onErroNovaCusta}
      />

      <DialogEditarCustas
        open={custaEmEdicao !== null}
        content={custaEmEdicao}
        idProcesso={idProcesso}
        onClose={onFecharDialogEditarCusta}
        onSucesso={onSucessoEditarCusta}
        onErro={onErroEditarCusta}
      />

      <Dialog
        open={acaoCustaAtiva !== null}
        onClose={enviandoAcaoCusta ? undefined : onCancelarAcaoCusta}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ pt: 4 }}>
          <DialogContentText sx={{ textAlign: 'center', fontSize: 16 }}>
            {acaoCustaAtiva?.tipo === 'excluir'
              ? 'Deseja excluir as Custas selecionada?'
              : 'Confirma o envio do valor das Custas na Conta Corrente do Cliente?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <ButtonOutlined onClick={onCancelarAcaoCusta} disabled={enviandoAcaoCusta}>Cancelar</ButtonOutlined>
          <ButtonUI onClick={onConfirmarAcaoCusta} disabled={enviandoAcaoCusta}>Confirmar</ButtonUI>
        </DialogActions>
      </Dialog>

      <CardWrapper>
        <SectionTitle
          right={!ehFaseAcordoSeguro && (
            <ButtonOutlined
              size="small"
              startIcon={<AddIcon />}
              disabled={!cobrancaJudicial || !hasPermissoes}
              onClick={onClickNovaCusta}
            >
              NOVO
            </ButtonOutlined>
          )}
        >
          Custas
        </SectionTitle>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 480 }}>
            <TableHead>
              <TableRow>
                <TableCell>Data</TableCell>
                <TableCell>Histórico</TableCell>
                <TableCell align="right">Valor</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {custas.map((custa) => {
                const bloqueada = acaoCustaBloqueada(custa);
                return (
                  <TableRow key={custa.idProcessoCustas}>
                    <TableCell>{toBrazilianDate(custa.dataLancamento)}</TableCell>
                    <TableCell>{custa.descricao}</TableCell>
                    <TableCell align="right">{formatBRL(custa.valor)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Lançar valor" arrow>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onClickLancarValorCusta(custa)}
                            disabled={bloqueada}
                            aria-label="Lançar valor da custa"
                          >
                            <PointOfSaleIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Editar" arrow>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onClickEditarCusta(custa)}
                            disabled={bloqueada}
                            aria-label="Editar custa"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Excluir" arrow>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onClickExcluirCusta(custa)}
                            disabled={bloqueada}
                            aria-label="Excluir custa"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {custas.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">Não existem custas para o processo</TableCell>
                  <TableCell />
                </TableRow>
              )}
              <TableRow sx={{ backgroundColor: C.paleGrey }}>
                <TableCell colSpan={2} />
                <TableCell align="right" sx={{ fontWeight: 700 }}>{formatBRL(totalCustas)}</TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </CardWrapper>
    </Box>
  );
}

AbaJuridico.propTypes = {
  dadosJuridicos: PropTypes.shape({
    estarComAdvogadoExterno: PropTypes.bool,
    detalhesValorCausa: PropTypes.string,
    valorCausa: PropTypes.number,
    estarLancadoValorCausaInicial: PropTypes.bool,
    estarLancadoValorCausaFinal: PropTypes.bool,
    porcentagemHonorarioContratualInicial: PropTypes.number,
    porcentagemHonorarioContratualFinal: PropTypes.number,
    dadosJuridicosCustasQueryList: PropTypes.arrayOf(PropTypes.shape({
      idProcessoCustas: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      dataLancamento: PropTypes.string,
      descricao: PropTypes.string,
      valor: PropTypes.number,
      estarLancado: PropTypes.bool,
    })),
  }),
  idProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onJuridicoMutado: PropTypes.func,
  cobrancaJudicial: PropTypes.bool,
  ehFaseAcordoSeguro: PropTypes.bool,
  hasPermissoes: PropTypes.bool,
};

AbaJuridico.defaultProps = {
  dadosJuridicos: null,
  idProcesso: null,
  onJuridicoMutado: null,
  cobrancaJudicial: false,
  ehFaseAcordoSeguro: false,
  hasPermissoes: false,
};
